import {
  Sidebar as BaseSidebar,
  SidebarMenu as BaseSidebarMenu,
  SidebarMenuButton as BaseSidebarMenuButton,
  cn,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenuItem,
  useSidebar,
} from '@1trading/react';
import { ChevronLeft, X } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { AuthUser } from '@/components/auth/auth-user';
import { EnvTag } from '@/components/ui/env-tag';
import { ThemeToggle } from '@/layouts/default/theme-toggle';
import { HOME_NAV_ITEM, isNavItemActive, isNavItemEnabled, NAV_GROUPS } from '@/layouts/nav';

// Style wrappers ported from exchange-ui (layouts/default/components/sidebar.tsx):
// the fixed button height + icon-mode padding keep icons the same size in both
// expanded and collapsed states.
const SidebarMenu = ({ className, ...props }: React.ComponentProps<typeof BaseSidebarMenu>) => <BaseSidebarMenu className={cn('w-auto -mx-1', className)} {...props} />;

const SidebarMenuButton = ({ className, ...props }: React.ComponentProps<typeof BaseSidebarMenuButton>) => (
  <BaseSidebarMenuButton className={cn('h-10! group-data-[collapsible=icon]:px-3!', className)} {...props} />
);

const SidebarMenuLinkText = ({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) => (
  <span className={cn('group-data-[collapsible=icon]:sr-only', className)} {...props} />
);

const AppSidebar = () => {
  const { pathname } = useLocation();
  const { isMobile, open, openMobile, setOpenMobile, toggleSidebar } = useSidebar();
  const isCollapsed = !(open || openMobile);
  // On mobile the sidebar is a Sheet that stays open over the page after navigating
  const closeMobile = () => setOpenMobile(false);
  const footerLabel = isMobile ? 'Close' : isCollapsed ? 'Expand' : 'Collapse';

  return (
    <BaseSidebar className="z-40 overflow-hidden border-none" collapsible="icon">
      <SidebarHeader className="py-4">
        <Link
          className="flex h-6 items-center gap-2 px-2 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:gap-0 group-data-[collapsible=icon]:px-0"
          onClick={closeMobile}
          to="/"
        >
          {/* max-w-none: Tailwind preflight's `img { max-width: 100% }` would squeeze the
              logo inside the collapsed rail's narrow flex box and distort it */}
          <img alt="One Trading" className="size-6 max-w-none shrink-0" src="https://assets.onetrading.com/img/onetrading-icon-rebrand-blue.svg" />
          <span className="truncate font-semibold group-data-[collapsible=icon]:sr-only">Ops Portal</span>
          <EnvTag className="ml-auto shrink-0 px-2 py-0 text-[10px] group-data-[collapsible=icon]:hidden" />
        </Link>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup className="pt-0">
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={pathname === HOME_NAV_ITEM.to} tooltip={HOME_NAV_ITEM.title}>
                  <Link onClick={closeMobile} to={HOME_NAV_ITEM.to}>
                    <HOME_NAV_ITEM.icon />
                    <SidebarMenuLinkText>{HOME_NAV_ITEM.title}</SidebarMenuLinkText>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        {NAV_GROUPS.map((group) => (
          <SidebarGroup key={group.title}>
            <SidebarGroupLabel>{group.title}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {group.items.filter(isNavItemEnabled).map(({ icon: Icon, ...item }) => (
                  <SidebarMenuItem key={item.to}>
                    <SidebarMenuButton asChild isActive={isNavItemActive(pathname, item.to)} tooltip={item.title}>
                      <Link onClick={closeMobile} to={item.to}>
                        <Icon />
                        <SidebarMenuLinkText>{item.title}</SidebarMenuLinkText>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>
      <SidebarFooter>
        <div className="flex items-center justify-between gap-2 group-data-[collapsible=icon]:flex-col">
          <AuthUser />
          <ThemeToggle />
        </div>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton onClick={isMobile ? closeMobile : toggleSidebar} tooltip={footerLabel}>
              {isMobile ? <X /> : <ChevronLeft className={isCollapsed ? 'rotate-180' : ''} />}
              <SidebarMenuLinkText>{footerLabel}</SidebarMenuLinkText>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </BaseSidebar>
  );
};
AppSidebar.displayName = 'AppSidebar';

export default AppSidebar;
